import dayjs, { Dayjs } from 'dayjs';
import { useFormikContext } from 'formik';
import get from 'lodash/get';
import { useCallback, useMemo } from 'react';
import { FormGeneratorOnSubmitParams, ReactFormGenerator } from 'react-form-builder';
import { useFetchAllRegisters } from '@/modules/common/hooks/useFetchAllRegisters.ts';
import ClientsSelectPaginated from '@/modules/admin-dashboard/components/ClientsSelectPaginated.tsx';
import { CalendarInput, SelectPaginateOption } from 'ui';
import { IncidentResultSchemaWithRel } from 'compliance-api';

interface DetailsProps {
  editing: boolean;
  formName: string;
  typeName: string;
  id?: string;
  isCompletionForm?: boolean;
  fromClientPage?: boolean;
}

const Details = ({
  editing,
  formName,
  typeName,
  id,
  isCompletionForm = false,
  fromClientPage = false,
}: DetailsProps) => {
  const formik = useFormikContext<{ incident: IncidentResultSchemaWithRel }>();

  const incident = formik.values.incident;

  const {
    user,
    form_results: _form_results,
    form_resolved_results: _form_resolved_results,
    date_occurred,
  } = incident;

  const { registers, isLoading: isLoadingRegisters } = useFetchAllRegisters();

  const formItems = useMemo(
    () => get(!isCompletionForm ? _form_results : _form_resolved_results, 'formItems', []),
    [isCompletionForm, _form_results, _form_resolved_results],
  );
  const formResults = useMemo(
    () => get(!isCompletionForm ? _form_results : _form_resolved_results, 'form_results', []),
    [isCompletionForm, _form_results, _form_resolved_results],
  );

  const adaptedQuestionsData = useMemo(
    () =>
      formResults.map((item: object) => {
        const isUrlInItem = 'url' in item && typeof item.url === 'string';
        const isValueInItem = 'value' in item;

        let value = get(item, 'value', '');

        if (isUrlInItem && !isValueInItem) {
          value = item.url as string;
        }

        return { ...item, value };
      }),
    [formResults],
  );

  const handleChangeDateOccurred = useCallback(
    (date: Dayjs) => {
      formik.setFieldValue('incident.date_occurred', date.toISOString());
    },
    [formik],
  );

  const handleChangeAnswers = useCallback(
    (answers: FormGeneratorOnSubmitParams[]) => {
      formik.setFieldValue(
        !isCompletionForm
          ? 'incident.form_results.form_results'
          : 'incident.form_resolved_results.form_results',
        answers,
      );
    },
    [formik, isCompletionForm],
  );

  const handleChangeItems = useCallback(
    (items: FormGeneratorOnSubmitParams[]) => {
      formik.setFieldValue(
        !isCompletionForm
          ? 'incident.form_results.formItems'
          : 'incident.form_resolved_results.formItems',
        items,
      );
    },
    [formik, isCompletionForm],
  );

  const handleSelectClient = useCallback(
    (option: SelectPaginateOption<number> | null) => {
      if (option?.value) {
        formik.setFieldValue('incident.client_id', option.value);
        formik.setFieldValue('incident.client', {
          id: option.value,
          name: option.label as string,
        });
      } else {
        formik.setFieldValue('incident.client_id', null);
        formik.setFieldValue('incident.client', null);
      }
    },
    [formik],
  );

  const clientOption = useMemo<SelectPaginateOption<number> | undefined>(() => {
    const currentClient = formik.values.incident.client;

    if (currentClient && currentClient.id && currentClient.name) {
      return {
        label: currentClient.name,
        value: currentClient.id,
      };
    }

    return undefined;
  }, [formik.values.incident.client]);

  return (
    <div className="pb-3 !max-w-xl">
      <div className="border-b py-10 px-2 sm:px-8">
        <div className="grid grid-cols-2 gap-8">
          <div className="flex flex-col gap-2.5">
            <div className="text-xs text-gray-500">DATE OCCURRED</div>
            <div>
              {editing ? (
                <CalendarInput
                  showTime
                  allowClear={false}
                  allowEmpty={false}
                  value={dayjs(date_occurred)}
                  onChange={handleChangeDateOccurred}
                  className="[&>div]:pt-0 [&>div]:pb-0.5"
                />
              ) : (
                dayjs(formik.values.incident.date_occurred).format('DD-MM-YYYY, HH:mm')
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2.5">
            <div className="text-[12px] text-gray-500">REPORTED BY</div>
            <div className="flex items-center gap-2">
              <span>
                {user?.first_name} {user?.last_name}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2.5">
            <span className="text-[12px] text-gray-500">FORM</span>
            <p>{formName}</p>
          </div>
          <div className="flex flex-col gap-2.5">
            <span className="text-[12px] text-gray-500">TYPE</span>
            <p>{typeName}</p>
          </div>
          <div className="flex flex-col gap-2.5">
            <span className="text-[12px] text-gray-500">ID</span>
            <p>{id}</p>
          </div>
          <div className={fromClientPage ? 'hidden' : 'flex flex-col gap-2.5'}>
            <span className="text-[12px] text-gray-500">CLIENT</span>
            {editing ? (
              <ClientsSelectPaginated
                isMulti={false}
                value={clientOption}
                onChange={handleSelectClient}
                label=""
                placeholder="Select client..."
                isClearable={true}
              />
            ) : (
              <p>{incident.client?.name || '-'}</p>
            )}
          </div>
        </div>
      </div>
      <div
        className="flex-1 overflow-auto px-2 sm:px-8 print:bg-white print:text-black print:p-10"
        id={'print-area'}
      >
        <ReactFormGenerator
          hide_actions
          useInitAnswers
          editing={editing}
          disabled={!editing}
          items={formItems}
          initAnswers={adaptedQuestionsData}
          onChange={handleChangeAnswers}
          onChangeItems={handleChangeItems}
          registers={registers}
          isRegistersLoading={isLoadingRegisters}
        />
      </div>
    </div>
  );
};

export default Details;
